/**
 * fallbackResolver.js
 * --------------------
 * Zero-result fallback content for every mini-matcher category.
 *
 * When a mini-matcher returns zero shortlisted businesses (coverage
 * state = 'concierge' / 'disabled' / fallback empty list), the user
 * lands on a soft "we're still onboarding ..." screen. Historically
 * that screen was a dead-end with just an email-capture field.
 *
 * This resolver returns category-aware **forward paths**:
 *   • `tools`  - 2-3 workspace tool tiles the user can jump into now.
 *   • `guides` - 2-3 long-form guide links specific to the matcher
 *                category, with read-time hints.
 *
 * Phase 2 (2026-02-15) - answer-aware variants:
 *   • Each category may declare a `when(answers)` function. If it
 *     returns a full `{ tools, guides }` payload, that overrides the
 *     `default`. Otherwise the `default` is used.
 *   • Lets us tailor the fallback by the user's refinement answers
 *     (e.g. inspection_type=advise → push terminology + concierge;
 *     timing=this_week → push Defects Scanner first).
 *
 * Adding a new category:
 *   1. Find the category_key the matcher uses (e.g. `partner:house_inspect`).
 *   2. Add an entry below with a `default` key (and optional `when`).
 *   3. Each tool/guide MUST include `label` and `url` (internal path).
 *
 * The shape is intentionally flat - no inheritance, no merging - so a
 * designer can scan one record and predict exactly what renders.
 */

// =====================================================================
// 1. Tool catalogue (one source of truth so labels stay consistent and
//    a renamed route only needs to change here).
// =====================================================================
const TOOLS = {
  defectsScanner:    { url: '/workspace/tools/defects-scanner',         label: 'Defects Scanner',         eyebrow: 'Flag issues' },
  buildProgress:     { url: '/workspace/tools/build-progress',          label: 'Build Progress',          eyebrow: 'Track stages' },
  projectHealth:     { url: '/workspace/tools/project-health',          label: 'Project Health',          eyebrow: 'See where you stand' },
  checklists:        { url: '/workspace/tools/checklists',              label: 'Build Checklists',        eyebrow: 'Self-inspect' },
  costTracker:       { url: '/workspace/tools/cost-tracker',            label: 'Cost Tracker',            eyebrow: 'Budget by stage' },
  quoteComparison:   { url: '/workspace/tools/quote-comparison',        label: 'AI Quote Comparison',     eyebrow: 'Compare apples to apples' },
  getQuote:          { url: '/workspace/tools/get-quote',               label: 'Get a Quote',             eyebrow: 'Brief us your project' },
  findABuilder:      { url: '/workspace/tools/find-a-builder',          label: 'Find a Builder Wizard',   eyebrow: '11-step matchmaker' },
  findATradie:       { url: '/workspace/tools/find-a-tradie',           label: 'Find a Tradie Hub',       eyebrow: 'Browse trades' },
  findASupplier:     { url: '/workspace/tools/find-a-supplier',         label: 'Find a Supplier',         eyebrow: 'Compare suppliers' },
  builderLicence:    { url: '/workspace/tools/builder-licence-check',   label: 'Builder Licence Check',   eyebrow: 'Verify credentials' },
  insuranceCheck:    { url: '/workspace/tools/building-insurance-check', label: 'Insurance Check',         eyebrow: 'Cover gaps' },
  facadeVisualiser:  { url: '/workspace/tools/facade-visualiser',       label: 'Facade Visualiser',       eyebrow: 'See it before you build' },
  interior3D:        { url: '/workspace/tools/interior-3d',             label: 'Interior 3D',             eyebrow: 'Walk-through layouts' },
  furniturePlanner:  { url: '/workspace/tools/furniture-planner',       label: 'Furniture Planner',       eyebrow: 'Plan the layout' },
  solarOrientation:  { url: '/workspace/tools/solar-orientation',       label: 'Solar Orientation',       eyebrow: 'Sun + shade check' },
  lifestyleDiscovery:{ url: '/workspace/tools/lifestyle-discovery',     label: 'Lifestyle Discovery',     eyebrow: 'Find your style' },
  hlMatchmaker:      { url: '/workspace/tools/house-and-land-matchmaker', label: 'H&L Matchmaker',        eyebrow: 'Match packages to budget' },
  hlExplorer:        { url: '/workspace/tools/house-and-land',          label: 'H&L Explorer',            eyebrow: 'Browse packages' },
  govResources:      { url: '/workspace/tools/government-resources',    label: 'Government Resources',    eyebrow: 'Grants + rebates' },
  documents:         { url: '/workspace/tools/documents',               label: 'Documents Hub',           eyebrow: 'Store your brief' },
  milestones:        { url: '/workspace/tools/milestones',              label: 'Milestones',              eyebrow: 'Plan the timeline' },
  contacts:          { url: '/workspace/tools/contacts-hub',            label: 'Contacts Hub',            eyebrow: 'Track everyone' },
  favouriteHomes:    { url: '/workspace/tools/favourite-homes',         label: 'Saved Designs',           eyebrow: 'Your shortlist' },
  intelligence:      { url: '/workspace/tools/intelligence',            label: 'BuildPilot Intelligence', eyebrow: 'Ask CoPilot' },
};

// =====================================================================
// 2. Shared guide presets - re-used across multiple categories so the
//    record stays tight.
// =====================================================================
const G = {
  // Builder + new build
  chooseBuilder:       { url: '/how-to-choose-a-builder-adelaide',         label: 'How to choose a builder',                    readMin: 8 },
  questionsBuilders:   { url: '/questions-to-ask-builders',                 label: '20 questions to ask builders',               readMin: 5 },
  builderChecklist:    { url: '/builder-selection-checklist',               label: 'Builder selection checklist',                readMin: 6 },
  readyToBuild:        { url: '/ready-to-build-checklist',                  label: 'Ready-to-build checklist',                   readMin: 3 },
  amIReady:            { url: '/am-i-ready-to-build',                       label: 'Am I ready to build?',                       readMin: 4 },
  buildCostsAdl:       { url: '/building-costs-adelaide-2026',              label: 'Adelaide build costs (2026)',                readMin: 6 },
  buildTimeline:       { url: '/building-a-new-home/build-timeline',        label: 'Typical build timeline',                     readMin: 5 },
  buildContracts:      { url: '/building-a-new-home/contracts',             label: 'Understanding the build contract',           readMin: 7 },

  // Renovation + extension
  renoPlanning:        { url: '/renovations/planning',                       label: 'Renovation planning guide',                  readMin: 6 },
  renoCosts:           { url: '/renovations/costs',                          label: 'What renovations actually cost',             readMin: 7 },
  renoChoose:          { url: '/renovations/choosing-a-renovator',           label: 'Choosing the right renovator',               readMin: 5 },
  renoLiving:          { url: '/renovations/living-through-renovation',      label: 'Living through a renovation',                readMin: 4 },
  whenToRebuild:       { url: '/knockdown-rebuild/when-to-knockdown-vs-renovate', label: 'Extend, renovate or rebuild?',           readMin: 6 },
  grannyFlat:          { url: '/granny-flat-guide',                          label: 'Granny flat & extension guide',              readMin: 8 },
  slopingBlock:        { url: '/sloping-block-guide',                        label: 'Sloping block solutions',                    readMin: 5 },
  kdrCosts:            { url: '/knockdown-rebuild/costs',                    label: 'Knockdown rebuild costs',                    readMin: 6 },
  kdrApprovals:        { url: '/knockdown-rebuild/council-approvals',        label: 'Council approvals for KDR',                  readMin: 5 },

  // Inspections + defects
  howInspections:      { url: '/buildinginspections',                        label: 'How building inspections work',              readMin: 4 },
  defectChecklist:     { url: '/builder-checklist-2026',                     label: 'Pre-handover defect checklist',              readMin: 6 },

  // Finance
  fhbHl:               { url: '/first-home-buyer-house-and-land-guide',      label: 'First home buyer H&L guide',                 readMin: 7 },
  hlHiddenCosts:       { url: '/house-and-land-package-hidden-costs',        label: 'Hidden costs in H&L packages',               readMin: 5 },
  fhbAdl:              { url: '/first-home-buyer-adelaide',                  label: 'First home buyer Adelaide guide',            readMin: 8 },
  fhbGrant:            { url: '/first-home-owner-grant-adelaide',            label: 'First home owner grant',                     readMin: 4 },
  buildVsBuy:          { url: '/build-vs-buy-adelaide',                      label: 'Build vs buy in Adelaide',                   readMin: 6 },

  // Outdoor / landscaping
  landscaping:         { url: '/landscaping',                                 label: 'Landscaping ideas + costs',                  readMin: 5 },
  swimmingPools:       { url: '/swimming-pools',                              label: 'Backyard pool guide',                        readMin: 6 },
  verandahs:           { url: '/verandahs-pergolas',                          label: 'Pergola & verandah guide',                   readMin: 5 },
  decking:             { url: '/decking',                                     label: 'Deck design + materials',                    readMin: 4 },
  shedsGarages:        { url: '/sheds-garages',                               label: 'Sheds & garages guide',                      readMin: 4 },

  // Interior / kitchens
  interiorDesign:      { url: '/interior-design',                             label: 'Interior design inspiration',                readMin: 4 },
  displayHomes:        { url: '/display-home-guide',                          label: 'Visiting display homes',                     readMin: 5 },

  // Energy + sustainability
  energyEfficient:     { url: '/energy-efficient-home-guide',                 label: 'Energy-efficient home guide',                readMin: 6 },
  nathers:             { url: '/terminology/nathers-rating',                  label: 'NatHERS rating explained',                   readMin: 3 },
  bal:                 { url: '/terminology/bal-rating',                      label: 'BAL rating (bushfire) explained',            readMin: 3 },

  // Hiring trades + pros
  hiringTrades:        { url: '/hiring-trades-guide',                         label: 'Hiring a tradie - what to know',             readMin: 5 },
  hiringPros:          { url: '/hiring-professionals-guide',                  label: 'Hiring professionals - what to know',        readMin: 5 },

  // Glossary / terminology
  terminology:         { url: '/azbuildingterminology',                       label: 'A-Z building terminology',                   readMin: 4 },
  slabOnGround:        { url: '/terminology/slab-on-ground',                  label: 'Slab-on-ground explained',                   readMin: 3 },

  // Council + suburb
  buildingFaq:         { url: '/building-faq',                                label: 'Building FAQ',                               readMin: 6 },
  buildGuideAdl:       { url: '/building-guide-adelaide',                     label: 'Complete Adelaide building guide',           readMin: 12 },

  // Display + journey
  hlPackages:          { url: '/house-and-land-packages-adelaide',            label: 'Browse H&L packages',                        readMin: 4 },
};

// =====================================================================
// 3. Template builders for category families (trades, suppliers, pros)
//    where the default fallback should be uniform. Override per-category
//    below if a bespoke set makes more sense.
// =====================================================================
function tradeFallback() {
  return {
    tools: [TOOLS.getQuote, TOOLS.costTracker, TOOLS.quoteComparison],
    guides: [G.hiringTrades, G.buildCostsAdl, G.buildingFaq],
  };
}
function supplierFallback() {
  return {
    tools: [TOOLS.findASupplier, TOOLS.quoteComparison, TOOLS.documents],
    guides: [G.interiorDesign, G.displayHomes, G.buildingFaq],
  };
}
function proFallback() {
  return {
    tools: [TOOLS.getQuote, TOOLS.documents, TOOLS.quoteComparison],
    guides: [G.hiringPros, G.buildingFaq, G.buildContracts],
  };
}

// =====================================================================
// 4. Per-category configs. Each value is:
//     { default: { tools, guides }, when?: (answers) => override | null }
//
//    `when()` returns a complete { tools, guides } override OR null to
//    fall through to `default`. No partial merges - keeps reasoning
//    about the fallback completely flat.
// =====================================================================
const FALLBACKS = {

  // ─────────────────────────────────────────────────────────────────
  // PARTNERS (Phase 2 - answer-aware)
  // ─────────────────────────────────────────────────────────────────
  'partner:house_inspect': {
    default: {
      tools: [TOOLS.defectsScanner, TOOLS.projectHealth, TOOLS.checklists],
      guides: [G.howInspections, G.defectChecklist, G.readyToBuild],
    },
    when: (a) => {
      // Urgent / mid-build users want a tool RIGHT NOW.
      if (a.timing === 'this_week' || a.timing === '2_weeks') {
        return {
          tools: [TOOLS.defectsScanner, TOOLS.checklists, TOOLS.projectHealth],
          guides: [G.defectChecklist, G.howInspections, G.readyToBuild],
        };
      }
      // "Not sure, advise me" → push education + terminology.
      if (a.inspection_type === 'advise') {
        return {
          tools: [TOOLS.checklists, TOOLS.projectHealth, TOOLS.intelligence],
          guides: [G.howInspections, G.terminology, G.defectChecklist],
        };
      }
      // Pre-purchase → land + KDR awareness instead of build-stage tools.
      if (a.inspection_type === 'pre_purchase') {
        return {
          tools: [TOOLS.hlExplorer, TOOLS.checklists, TOOLS.documents],
          guides: [G.buildVsBuy, G.whenToRebuild, G.kdrApprovals],
        };
      }
      return null;
    },
  },

  'partner:equable': {
    default: {
      tools: [TOOLS.costTracker, TOOLS.quoteComparison, TOOLS.hlMatchmaker],
      guides: [G.fhbHl, G.hlHiddenCosts, G.amIReady],
    },
    when: (a) => {
      // First home buyer → push the FHB grant + first-home guide.
      if (a.first_home_buyer === 'yes') {
        return {
          tools: [TOOLS.costTracker, TOOLS.hlMatchmaker, TOOLS.govResources],
          guides: [G.fhbAdl, G.fhbGrant, G.fhbHl],
        };
      }
      // Refinance → existing-home focus, not new-build.
      if (a.finance_for === 'refinance') {
        return {
          tools: [TOOLS.costTracker, TOOLS.documents, TOOLS.govResources],
          guides: [G.buildVsBuy, G.amIReady, G.buildingFaq],
        };
      }
      // Renovation finance → reno content.
      if (a.finance_for === 'renovation') {
        return {
          tools: [TOOLS.costTracker, TOOLS.quoteComparison, TOOLS.facadeVisualiser],
          guides: [G.renoCosts, G.renoPlanning, G.whenToRebuild],
        };
      }
      // Not pre-approved + just starting → push readiness content.
      if (a.preapproval === 'no') {
        return {
          tools: [TOOLS.costTracker, TOOLS.intelligence, TOOLS.hlMatchmaker],
          guides: [G.amIReady, G.fhbGrant, G.buildVsBuy],
        };
      }
      return null;
    },
  },

  // ─────────────────────────────────────────────────────────────────
  // BUILDERS (Phase 2 - answer-aware for build_goal)
  // ─────────────────────────────────────────────────────────────────
  'professional:builder': {
    default: {
      tools: [TOOLS.findABuilder, TOOLS.quoteComparison, TOOLS.builderLicence],
      guides: [G.chooseBuilder, G.questionsBuilders, G.builderChecklist],
    },
    when: (a) => {
      // Knockdown rebuild path → KDR-specific content.
      if (a.build_goal === 'kdr' || a.build_type === 'kdr') {
        return {
          tools: [TOOLS.findABuilder, TOOLS.costTracker, TOOLS.facadeVisualiser],
          guides: [G.kdrCosts, G.kdrApprovals, G.whenToRebuild],
        };
      }
      // First home buyer
      if (a.build_goal === 'first_home' || a.has_land === 'no') {
        return {
          tools: [TOOLS.hlMatchmaker, TOOLS.costTracker, TOOLS.findABuilder],
          guides: [G.fhbAdl, G.fhbHl, G.fhbGrant],
        };
      }
      // Sloping block
      if (a.slope && a.slope !== 'flat' && a.slope !== 'unsure') {
        return {
          tools: [TOOLS.findABuilder, TOOLS.solarOrientation, TOOLS.quoteComparison],
          guides: [G.slopingBlock, G.chooseBuilder, G.builderChecklist],
        };
      }
      return null;
    },
  },

  'professional:renovation_builder': {
    default: {
      tools: [TOOLS.quoteComparison, TOOLS.costTracker, TOOLS.interior3D],
      guides: [G.renoPlanning, G.renoCosts, G.renoChoose],
    },
    when: (a) => {
      if (a.reno_scope === 'whole_home') {
        return {
          tools: [TOOLS.costTracker, TOOLS.quoteComparison, TOOLS.facadeVisualiser],
          guides: [G.whenToRebuild, G.renoCosts, G.renoLiving],
        };
      }
      if (a.reno_scope === 'single_room') {
        return {
          tools: [TOOLS.interior3D, TOOLS.furniturePlanner, TOOLS.quoteComparison],
          guides: [G.renoCosts, G.renoPlanning, G.interiorDesign],
        };
      }
      if (a.reno_scope === 'heritage') {
        return {
          tools: [TOOLS.documents, TOOLS.quoteComparison, TOOLS.intelligence],
          guides: [G.renoPlanning, G.renoChoose, G.kdrApprovals],
        };
      }
      return null;
    },
  },

  'professional:extension_builder': {
    default: {
      tools: [TOOLS.quoteComparison, TOOLS.costTracker, TOOLS.facadeVisualiser],
      guides: [G.whenToRebuild, G.grannyFlat, G.slopingBlock],
    },
    when: (a) => {
      if (a.extension_type === 'granny_flat') {
        return {
          tools: [TOOLS.facadeVisualiser, TOOLS.costTracker, TOOLS.quoteComparison],
          guides: [G.grannyFlat, G.kdrApprovals, G.slopingBlock],
        };
      }
      if (a.extension_type === 'second_storey') {
        return {
          tools: [TOOLS.facadeVisualiser, TOOLS.costTracker, TOOLS.interior3D],
          guides: [G.whenToRebuild, G.kdrCosts, G.kdrApprovals],
        };
      }
      if (a.drawings_ready === 'no_drawings') {
        return {
          tools: [TOOLS.getQuote, TOOLS.documents, TOOLS.facadeVisualiser],
          guides: [G.renoPlanning, G.whenToRebuild, G.grannyFlat],
        };
      }
      return null;
    },
  },

  // ─────────────────────────────────────────────────────────────────
  // OUTDOOR / SPECIALIST BUILDERS
  // ─────────────────────────────────────────────────────────────────
  'professional:pergola_builder':  { default: { tools: [TOOLS.facadeVisualiser, TOOLS.getQuote, TOOLS.quoteComparison], guides: [G.verandahs, G.decking, G.landscaping] } },
  'professional:deck_builder':     { default: { tools: [TOOLS.facadeVisualiser, TOOLS.getQuote, TOOLS.quoteComparison], guides: [G.decking, G.verandahs, G.landscaping] } },
  'professional:shed_builder':     { default: { tools: [TOOLS.facadeVisualiser, TOOLS.getQuote, TOOLS.solarOrientation], guides: [G.shedsGarages, G.landscaping, G.verandahs] } },
  'tradie:pool_builder':           { default: { tools: [TOOLS.getQuote, TOOLS.costTracker, TOOLS.solarOrientation], guides: [G.swimmingPools, G.landscaping, G.verandahs] } },

  'tradie:landscaper': {
    default: { tools: [TOOLS.getQuote, TOOLS.solarOrientation, TOOLS.lifestyleDiscovery], guides: [G.landscaping, G.verandahs, G.swimmingPools] },
    when: (a) => {
      if (a.land_scope === 'paving' || a.land_scope === 'retaining') {
        return { tools: [TOOLS.getQuote, TOOLS.quoteComparison, TOOLS.costTracker], guides: [G.landscaping, G.slopingBlock, G.buildingFaq] };
      }
      if (a.land_scope === 'decking_pergola') {
        return { tools: [TOOLS.facadeVisualiser, TOOLS.getQuote, TOOLS.quoteComparison], guides: [G.decking, G.verandahs, G.landscaping] };
      }
      return null;
    },
  },

  // ─────────────────────────────────────────────────────────────────
  // KITCHENS / INTERIOR
  // ─────────────────────────────────────────────────────────────────
  'professional:kitchens': {
    default: {
      tools: [TOOLS.interior3D, TOOLS.furniturePlanner, TOOLS.quoteComparison],
      guides: [G.interiorDesign, G.displayHomes, G.renoCosts],
    },
    when: (a) => {
      if (a.kitchen_scope === 'cabinetry_only') {
        return {
          tools: [TOOLS.findATradie, TOOLS.quoteComparison, TOOLS.documents],
          guides: [G.hiringTrades, G.renoCosts, G.interiorDesign],
        };
      }
      if (a.kitchen_scope === 'full_reno') {
        return {
          tools: [TOOLS.interior3D, TOOLS.costTracker, TOOLS.quoteComparison],
          guides: [G.renoPlanning, G.renoCosts, G.interiorDesign],
        };
      }
      return null;
    },
  },

  'professional:interior_designer': { default: { tools: [TOOLS.interior3D, TOOLS.furniturePlanner, TOOLS.lifestyleDiscovery], guides: [G.interiorDesign, G.displayHomes, G.renoPlanning] } },
  'professional:building_designer': { default: { tools: [TOOLS.facadeVisualiser, TOOLS.interior3D, TOOLS.quoteComparison], guides: [G.chooseBuilder, G.builderChecklist, G.buildContracts] } },
  'professional:architect':         { default: { tools: [TOOLS.facadeVisualiser, TOOLS.interior3D, TOOLS.quoteComparison], guides: [G.chooseBuilder, G.buildContracts, G.renoPlanning] } },

  // ─────────────────────────────────────────────────────────────────
  // ADMIN / LEGAL / FINANCE PRO SERVICES
  // ─────────────────────────────────────────────────────────────────
  'professional:surveyor':           { default: { tools: [TOOLS.getQuote, TOOLS.documents, TOOLS.govResources], guides: [G.slopingBlock, G.kdrApprovals, G.buildingFaq] } },
  'professional:structural_engineer':{ default: { tools: [TOOLS.getQuote, TOOLS.documents, TOOLS.quoteComparison], guides: [G.slopingBlock, G.buildingFaq, G.terminology] } },
  'professional:civil_engineer':     { default: { tools: [TOOLS.getQuote, TOOLS.documents, TOOLS.quoteComparison], guides: [G.slopingBlock, G.buildingFaq, G.kdrApprovals] } },
  'professional:conveyancer':        { default: { tools: [TOOLS.documents, TOOLS.govResources, TOOLS.checklists], guides: [G.buildContracts, G.fhbHl, G.amIReady] } },
  'professional:property_lawyer':    { default: { tools: [TOOLS.documents, TOOLS.govResources, TOOLS.checklists], guides: [G.buildContracts, G.amIReady, G.buildingFaq] } },
  'professional:insurance_broker':   { default: { tools: [TOOLS.insuranceCheck, TOOLS.documents, TOOLS.govResources], guides: [G.buildContracts, G.buildingFaq, G.amIReady] } },
  'professional:quantity_surveyor':  { default: { tools: [TOOLS.costTracker, TOOLS.quoteComparison, TOOLS.documents], guides: [G.buildCostsAdl, G.buildContracts, G.buildingFaq] } },

  'professional:energy_assessor': {
    default: {
      tools: [TOOLS.solarOrientation, TOOLS.documents, TOOLS.getQuote],
      guides: [G.energyEfficient, G.nathers, G.buildingFaq],
    },
    when: (a) => {
      if (a.assessment_type === 'new_nathers') {
        return { tools: [TOOLS.solarOrientation, TOOLS.facadeVisualiser, TOOLS.documents], guides: [G.nathers, G.energyEfficient, G.buildContracts] };
      }
      return null;
    },
  },

  'professional:town_planner': {
    default: {
      tools: [TOOLS.govResources, TOOLS.documents, TOOLS.getQuote],
      guides: [G.kdrApprovals, G.buildingFaq, G.slopingBlock],
    },
    when: (a) => {
      // Council-specific guides if we know which council.
      const COUNCIL_GUIDES = {
        onkaparinga:    { url: '/building-in-onkaparinga-council',    label: 'Building in Onkaparinga Council',     readMin: 5 },
        salisbury:      { url: '/building-in-salisbury-council',      label: 'Building in Salisbury Council',       readMin: 5 },
        playford:       { url: '/building-in-playford-council',       label: 'Building in Playford Council',        readMin: 5 },
        mount_barker:   { url: '/building-in-mount-barker-council',   label: 'Building in Mount Barker Council',    readMin: 5 },
        charles_sturt:  { url: '/building-in-charles-sturt-council',  label: 'Building in Charles Sturt Council',   readMin: 5 },
        tea_tree_gully: { url: '/building-in-tea-tree-gully-council', label: 'Building in Tea Tree Gully Council',  readMin: 5 },
      };
      const councilGuide = a.council_known && COUNCIL_GUIDES[a.council_known];
      if (councilGuide) {
        return {
          tools: [TOOLS.govResources, TOOLS.documents, TOOLS.getQuote],
          guides: [councilGuide, G.kdrApprovals, G.buildingFaq],
        };
      }
      return null;
    },
  },

  // ─────────────────────────────────────────────────────────────────
  // TRADIES - mostly the generic tradeFallback() with bespoke
  // overrides where a tool/guide clearly maps to the trade.
  // ─────────────────────────────────────────────────────────────────
  'tradie:electrician':    { default: { tools: [TOOLS.getQuote, TOOLS.costTracker, TOOLS.quoteComparison], guides: [G.hiringTrades, G.buildingFaq, G.terminology] } },
  'tradie:plumber':        { default: { tools: [TOOLS.getQuote, TOOLS.costTracker, TOOLS.quoteComparison], guides: [G.hiringTrades, G.buildingFaq, G.renoCosts] } },
  'tradie:painter':        { default: { tools: [TOOLS.getQuote, TOOLS.interior3D, TOOLS.quoteComparison], guides: [G.hiringTrades, G.interiorDesign, G.renoCosts] } },
  'tradie:tiler':          { default: { tools: [TOOLS.getQuote, TOOLS.interior3D, TOOLS.quoteComparison], guides: [G.hiringTrades, G.interiorDesign, G.renoCosts] } },
  'tradie:carpenter':      { default: tradeFallback() },
  'tradie:roofer':         { default: { tools: [TOOLS.getQuote, TOOLS.solarOrientation, TOOLS.quoteComparison], guides: [G.hiringTrades, G.energyEfficient, G.buildingFaq] } },
  'tradie:concreter':      { default: { tools: [TOOLS.getQuote, TOOLS.costTracker, TOOLS.quoteComparison], guides: [G.hiringTrades, G.slabOnGround, G.slopingBlock] } },
  'tradie:bricklayer':     { default: tradeFallback() },
  'tradie:cabinetmaker':   { default: { tools: [TOOLS.interior3D, TOOLS.furniturePlanner, TOOLS.quoteComparison], guides: [G.interiorDesign, G.renoCosts, G.hiringTrades] } },
  'tradie:glazier':        { default: { tools: [TOOLS.getQuote, TOOLS.facadeVisualiser, TOOLS.quoteComparison], guides: [G.energyEfficient, G.hiringTrades, G.renoCosts] } },
  'tradie:renderer':       { default: { tools: [TOOLS.getQuote, TOOLS.facadeVisualiser, TOOLS.quoteComparison], guides: [G.hiringTrades, G.renoCosts, G.buildingFaq] } },
  'tradie:plasterer':      { default: tradeFallback() },
  'tradie:hvac':           { default: { tools: [TOOLS.getQuote, TOOLS.solarOrientation, TOOLS.quoteComparison], guides: [G.energyEfficient, G.nathers, G.hiringTrades] } },
  'tradie:security':       { default: { tools: [TOOLS.getQuote, TOOLS.contacts, TOOLS.quoteComparison], guides: [G.hiringTrades, G.buildingFaq, G.renoCosts] } },
  'tradie:solar': {
    default: { tools: [TOOLS.solarOrientation, TOOLS.getQuote, TOOLS.govResources], guides: [G.energyEfficient, G.nathers, G.buildingFaq] },
  },
  'tradie:fencer':         { default: { tools: [TOOLS.getQuote, TOOLS.facadeVisualiser, TOOLS.quoteComparison], guides: [G.landscaping, G.hiringTrades, G.buildingFaq] } },
  'tradie:paver':          { default: { tools: [TOOLS.getQuote, TOOLS.facadeVisualiser, TOOLS.quoteComparison], guides: [G.landscaping, G.hiringTrades, G.slopingBlock] } },
  'tradie:retaining_wall': { default: { tools: [TOOLS.getQuote, TOOLS.facadeVisualiser, TOOLS.quoteComparison], guides: [G.slopingBlock, G.landscaping, G.hiringTrades] } },

  // ─────────────────────────────────────────────────────────────────
  // SUPPLIERS - default supplier template, bespoke for surfaces that
  // map cleanly to a tool.
  // ─────────────────────────────────────────────────────────────────
  'supplier:flooring':         { default: { tools: [TOOLS.findASupplier, TOOLS.interior3D, TOOLS.quoteComparison], guides: [G.interiorDesign, G.displayHomes, G.renoCosts] } },
  'supplier:stone_benchtops':  { default: { tools: [TOOLS.findASupplier, TOOLS.interior3D, TOOLS.quoteComparison], guides: [G.interiorDesign, G.renoCosts, G.displayHomes] } },
  'supplier:windows_doors':    { default: { tools: [TOOLS.findASupplier, TOOLS.facadeVisualiser, TOOLS.quoteComparison], guides: [G.energyEfficient, G.nathers, G.displayHomes] } },
  'professional:windows_doors':{ default: { tools: [TOOLS.facadeVisualiser, TOOLS.findASupplier, TOOLS.quoteComparison], guides: [G.energyEfficient, G.nathers, G.displayHomes] } },
  'supplier:solar_products':   { default: { tools: [TOOLS.solarOrientation, TOOLS.findASupplier, TOOLS.quoteComparison], guides: [G.energyEfficient, G.nathers, G.displayHomes] } },
  'supplier:roofing_products': { default: { tools: [TOOLS.findASupplier, TOOLS.facadeVisualiser, TOOLS.quoteComparison], guides: [G.energyEfficient, G.displayHomes, G.bal] } },
  'supplier:cladding':         { default: { tools: [TOOLS.findASupplier, TOOLS.facadeVisualiser, TOOLS.quoteComparison], guides: [G.energyEfficient, G.bal, G.displayHomes] } },
  'supplier:appliances':       { default: { tools: [TOOLS.findASupplier, TOOLS.furniturePlanner, TOOLS.quoteComparison], guides: [G.interiorDesign, G.energyEfficient, G.displayHomes] } },
  'supplier:tapware':          { default: { tools: [TOOLS.findASupplier, TOOLS.interior3D, TOOLS.quoteComparison], guides: [G.interiorDesign, G.displayHomes, G.renoCosts] } },
  'supplier:tiles':            { default: { tools: [TOOLS.findASupplier, TOOLS.interior3D, TOOLS.quoteComparison], guides: [G.interiorDesign, G.displayHomes, G.renoCosts] } },
  'supplier:lighting':         { default: { tools: [TOOLS.findASupplier, TOOLS.interior3D, TOOLS.quoteComparison], guides: [G.interiorDesign, G.energyEfficient, G.displayHomes] } },
  'supplier:furniture':        { default: { tools: [TOOLS.furniturePlanner, TOOLS.findASupplier, TOOLS.lifestyleDiscovery], guides: [G.interiorDesign, G.displayHomes, G.renoCosts] } },
  'supplier:blinds_curtains':  { default: { tools: [TOOLS.findASupplier, TOOLS.interior3D, TOOLS.quoteComparison], guides: [G.interiorDesign, G.displayHomes, G.renoCosts] } },
  'supplier:garage_doors':     { default: { tools: [TOOLS.findASupplier, TOOLS.facadeVisualiser, TOOLS.quoteComparison], guides: [G.shedsGarages, G.displayHomes, G.hiringTrades] } },
  'supplier:building_materials':{ default: { tools: [TOOLS.findASupplier, TOOLS.costTracker, TOOLS.quoteComparison], guides: [G.buildCostsAdl, G.hiringTrades, G.buildingFaq] } },
  'supplier:smart_home':       { default: { tools: [TOOLS.findASupplier, TOOLS.interior3D, TOOLS.quoteComparison], guides: [G.energyEfficient, G.interiorDesign, G.displayHomes] } },
  'supplier:outdoor_kitchen':  { default: { tools: [TOOLS.findASupplier, TOOLS.facadeVisualiser, TOOLS.quoteComparison], guides: [G.landscaping, G.verandahs, G.interiorDesign] } },
  'supplier:pools_spa_products':{ default: { tools: [TOOLS.findASupplier, TOOLS.solarOrientation, TOOLS.quoteComparison], guides: [G.swimmingPools, G.landscaping, G.energyEfficient] } },
};

// =====================================================================
// 5. Suburb-aware pivot helpers
//
// Three families of suburb-specific landing pages exist on the site:
//   • /best-builder-{slug}           - 12 builder-specific suburb pages
//   • /building-in/{slug}            -  9 council/planning suburb pages
//   • /south-australia/adelaide/{slug} - 12 general area pages
//
// If the matcher captured a suburb that maps to one of these surfaces,
// we PREPEND a suburb-specific guide to the resolved guides[] (and
// trim to keep the list at 3 max). The category decides which family
// to prefer.
// =====================================================================

// Suburbs with a dedicated /best-builder-{slug} page (builder context).
const BEST_BUILDER_SUBURBS = new Set([
  'aldinga', 'angle-vale', 'elizabeth', 'gawler', 'golden-grove',
  'mawson-lakes', 'morphett-vale', 'mount-barker', 'munno-para',
  'seaford', 'two-wells',
]);

// Suburbs with a dedicated /building-in/{slug} page (council/planning context).
const BUILDING_IN_SUBURBS = new Set([
  'bridgewater', 'davoren-park', 'hahndorf', 'littlehampton',
  'lobethal', 'nairne', 'penfield', 'stirling', 'woodside',
]);

// Suburbs with a dedicated /south-australia/adelaide/{slug} area page.
const ADELAIDE_AREA_SUBURBS = new Set([
  'brighton', 'burnside', 'fulham', 'glenelg', 'henley-beach',
  'magill', 'marden', 'mitcham', 'norwood', 'plympton', 'prospect',
  'unley',
]);

// Categories where "/best-builder-{suburb}" is the most relevant
// suburb pivot. Everything else (trades, suppliers, finance, legal)
// gets the general area page or building-in/ page when available.
const BUILDER_CONTEXT_CATEGORIES = new Set([
  'professional:builder',
  'professional:renovation_builder',
  'professional:extension_builder',
  'professional:building_designer',
  'professional:architect',
  'professional:pergola_builder',
  'professional:deck_builder',
  'professional:shed_builder',
  'tradie:pool_builder',
]);

function slugifySuburb(s) {
  return String(s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function titleizeSuburb(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
    .split(/[\s-]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Return a single suburb-specific guide tile if one of the 33 known
 * suburb pages matches, prioritised by category. Returns null when
 * we don't have a dedicated suburb page (the fallback simply shows
 * the standard category guides).
 */
function resolveSuburbGuide(suburb, categoryKey) {
  if (!suburb) return null;
  const slug = slugifySuburb(suburb);
  if (!slug) return null;
  const title = titleizeSuburb(suburb);
  const isBuilderCtx = BUILDER_CONTEXT_CATEGORIES.has(categoryKey);

  // Builder-context categories prefer the /best-builder-{slug} page
  // because it's the closest content match for "who builds here?"
  if (isBuilderCtx && BEST_BUILDER_SUBURBS.has(slug)) {
    return {
      url: `/best-builder-${slug}`,
      label: `Top builders in ${title}`,
      readMin: 4,
      isSuburb: true,
    };
  }
  // Otherwise prefer /building-in/{slug} (council/planning depth)
  if (BUILDING_IN_SUBURBS.has(slug)) {
    return {
      url: `/building-in/${slug}`,
      label: `Building in ${title}`,
      readMin: 5,
      isSuburb: true,
    };
  }
  // Otherwise the general area page
  if (ADELAIDE_AREA_SUBURBS.has(slug)) {
    return {
      url: `/south-australia/adelaide/${slug}`,
      label: `${title} - area guide`,
      readMin: 4,
      isSuburb: true,
    };
  }
  // Final fallback for builder-context categories - the best-builder
  // page list (covers "any suburb the user typed" even if we don't
  // have a dedicated page for it yet).
  if (isBuilderCtx && BEST_BUILDER_SUBURBS.has(slug)) {
    return {
      url: `/best-builder-${slug}`,
      label: `Top builders in ${title}`,
      readMin: 4,
      isSuburb: true,
    };
  }
  return null;
}

// =====================================================================
// 6. Public API
// =====================================================================
/**
 * Resolve fallback content for a given matcher category, optionally
 * tailored by the user's refinement answers and captured suburb.
 *
 * @param {string} categoryKey  e.g. 'partner:house_inspect'
 * @param {object} [answers]    refinement_answers map, e.g. { timing: 'this_week' }
 * @param {object} [ctx]        optional context, currently { suburb }.
 *                              Suburb may come from the matcher account
 *                              form or any *_suburb refinement question.
 * @returns {{tools: Array, guides: Array} | null}
 */
export function resolveFallback(categoryKey, answers = {}, ctx = {}) {
  if (!categoryKey) return null;
  const cfg = FALLBACKS[categoryKey];
  if (!cfg) return null;
  let picked = null;
  if (typeof cfg.when === 'function') {
    try {
      const override = cfg.when(answers || {});
      if (override && (override.tools || override.guides)) picked = override;
    } catch (_) {
      // never let a buggy override blow up the popup
      picked = null;
    }
  }
  const payload = picked || cfg.default || null;
  if (!payload) return null;

  let guides = Array.isArray(payload.guides) ? payload.guides.filter(Boolean) : [];

  // Prepend a suburb-aware guide when we have a matching dedicated page.
  // Keep the guide list at 3 by dropping the last existing one.
  const suburb = ctx && ctx.suburb;
  if (suburb) {
    const suburbGuide = resolveSuburbGuide(suburb, categoryKey);
    if (suburbGuide) {
      // Avoid duplicates if the same URL was somehow already in the list.
      const dedup = guides.filter((g) => g && g.url !== suburbGuide.url);
      guides = [suburbGuide, ...dedup].slice(0, 3);
    }
  }

  return {
    tools:  Array.isArray(payload.tools)  ? payload.tools.filter(Boolean)  : [],
    guides,
  };
}

export default resolveFallback;
